// src/contexts/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { 
  registerWithEmailAndPassword, 
  loginWithEmailAndPassword, 
  signInWithGoogle,
  completeGoogleSignup,
  logOut, 
  resetPassword 
} from '../services/auth';

// Create Context
const AuthContext = createContext();

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        try {
          // Get user type from Firestore
          const userDoc = await getDoc(doc(db, "users", user.uid));
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserType(userData.userType);
            
            // Get user profile based on user type
            if (userData.userType) {
              const profileDoc = await getDoc(doc(db, userData.userType, user.uid));
              
              if (profileDoc.exists()) {
                setUserProfile(profileDoc.data());
              }
            }
          }
        } catch (err) {
          console.error("Error fetching user data:", err);
        }
      } else {
        // Reset state when user logs out
        setUserType(null);
        setUserProfile(null);
      }
      
      setLoading(false);
    });
    
    // Cleanup subscription
    return () => unsubscribe();
  }, []); // Remove sessionRole dependency
  
  // Register function
  const register = async (email, password, userType, displayName) => {
    setError(null);
    try {
      const user = await registerWithEmailAndPassword(email, password, userType, displayName);
      return user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };
  
  // Login function
  const login = async (email, password) => {
    setError(null);
    try {
      const userCredential = await loginWithEmailAndPassword(email, password);
      
      if (!userCredential || !userCredential.user) {
        throw new Error('Failed to get user credentials');
      }
      
      // Important: Wait for user type to be loaded before navigating
      const userDoc = await getDoc(doc(db, "users", userCredential.user.uid));
      
      let userType = null;
      if (userDoc.exists()) {
        const userData = userDoc.data();
        userType = userData.userType; // Set user type in state
        setUserType(userType); 
        
        // Also load user profile
        if (userData.userType) {
          const profileDoc = await getDoc(doc(db, userData.userType, userCredential.user.uid));
          if (profileDoc.exists()) {
            setUserProfile(profileDoc.data());
          }
        }
      }
      
      return { user: userCredential.user, userType };
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Google Sign-in function
  const loginWithGoogle = async () => {
    setError(null);
    try {
      const result = await signInWithGoogle();
      
      if (!result.isNewUser && result.userType) {
        setUserType(result.userType); // Set user type in state
        
        // Also load user profile
        const profileDoc = await getDoc(doc(db, result.userType, result.user.uid));
        if (profileDoc.exists()) {
          setUserProfile(profileDoc.data());
        }
      }
      
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };
  
  // Complete Google signup with user type
  const completeGoogleRegistration = async (uid, userType) => {
    setError(null);
    try {
      const result = await completeGoogleSignup(uid, userType);
      setUserType(userType);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };
  
  // Logout function
  const logout = async () => {
    setError(null);
    try {
      await logOut();
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };
  
  // Alternative logout function names for compatibility
  const signOut = logout;
  const handleLogout = logout;
  
  // Password reset function
  const passwordReset = async (email) => {
    setError(null);
    try {
      await resetPassword(email);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };
  
  // Refresh user profile with better error handling
  const refreshUserProfile = async () => {
    if (!currentUser) return;
    
    try {
      if (userType) {
        const profileDoc = await getDoc(doc(db, userType, currentUser.uid));
        
        if (profileDoc.exists()) {
          setUserProfile(profileDoc.data());
        }
      }
    } catch (err) {
      console.error("Error refreshing user profile:", err);
      // Only set error for critical failures, not permission issues
      if (err.code !== 'permission-denied') {
        setError(`Failed to refresh profile: ${err.message}`);
      }
    }
  };
  
  // Check if user is a provider
  const isProvider = () => {
    return userType === 'providers';
  };
  
  // Check if user is a parent
  const isParent = () => {
    return userType === 'parents';
  };
  
  // Check if user is a job seeker
  const isJobSeeker = () => {
    return userType === 'jobseekers';
  };
  
  // Context value
  const value = {
    currentUser,
    userType,
    userProfile,
    loading,
    error,
    register,
    login,
    loginWithGoogle,
    completeGoogleRegistration,
    logout,
    signOut,
    handleLogout,
    passwordReset,
    refreshUserProfile,
    isProvider,
    isParent,
    isJobSeeker
  };
  
  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

// Custom hook for using the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

export default AuthContext;